/// <reference path="../../types/images.d.ts" />
import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Button from "../ui/Button";
import { useAppSelector, useAppDispatch } from "../../store/hooks";
import { RootState } from "../../store";
import {
  markNotificationRead,
  markAllNotificationsRead,
  fetchNotifications,
  fetchUnreadCount,
} from "../../features/notifications/thunks/notifications.thunk";
import { useThrottledNotificationFetch } from "../../features/notifications/hooks/useThrottledNotificationFetch";
import { logoutUser } from "../../features/auth/thunks/auth.thunk";
import { logout } from "../../features/auth/slices/auth.slice";
import { resetNotifications } from "../../features/notifications/slices/notifications.slice";
import { isMessageOrReactionNotification, getConversationIdFromNotification, getActorIdFromNotification } from "../../features/notifications/utils/notificationActor.utils";

import logo from "../../assets/images/logos/ba-Primary-brand-logo-colored.png";
import logoIcon from "../../assets/images/logos/ba-brand-icon-colored.png";
import logoPre from "../../assets/images/pre/logo.png";
import avatar from "../../assets/images/logos/avatar.png";
import notificationIcon from "../../assets/images/pre/notification.svg";
import BaoNotificationDropdown from "../modals/BaoNotificationDropdown";
import BaoDesktopMenuDropdown from "../modals/BaoDesktopMenuDropdown";
import BaoMobileMenuDropdown from "../modals/BaoMobileMenuDropdown";

interface HeaderProps {
  showSearchBar?: boolean;
  isProductDetailPage?: boolean;
}

const Header: React.FC<HeaderProps> = ({
  showSearchBar = false,
  isProductDetailPage = false,
}) => {

  const user = useAppSelector((state: RootState) => state.auth.user);
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDesktopMenuOpen, setIsDesktopMenuOpen] = useState(false);

  const [highlightChats, setHighlightChats] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [notificationTab, setNotificationTab] = useState<'all' | 'unread' | 'messages'>('all');
  const dispatch = useAppDispatch();
  const { items: notifications, unreadCount: notificationCount } = useAppSelector((state: RootState) => state.notifications);

  const markAsRead = (id: string) => dispatch(markNotificationRead(id));
  const markAllAsReadContext = () => dispatch(markAllNotificationsRead());

  useThrottledNotificationFetch(isNotificationOpen);
  const markAllAsRead = async () => {
    await markAllAsReadContext();
    setNotificationTab('all');
  };

  const filteredNotifications = notifications.filter(notif => {
    if (notificationTab === 'all') return true;
    if (notificationTab === 'unread') return !notif.isRead;
    if (notificationTab === 'messages') return notif.type === 'message' || notif.type === 'NEW_MESSAGE';
    return true;
  });

  // Ensure notification state is isolated per account
  useEffect(() => {
    if (!user?.id) {
      dispatch(resetNotifications());
      return;
    }
    dispatch(fetchUnreadCount());
  }, [user?.id, dispatch]);

  const handleLogoClick = (e: React.MouseEvent) => {
    if (user && (!user.firstName || !user.lastName)) {
      e.preventDefault();
      navigate('/profile-setup');
    }
  };

  const handleLogout = async () => {
    try {
      // Use the async thunk for a full cleanup (server + client storage)
      await dispatch(logoutUser()).unwrap();
    } catch (error) {
      console.error("Logout failed:", error);
      // Fallback: forcefully clear local state if thunk fails
      dispatch(logout());
    } finally {
      setIsMobileMenuOpen(false);
      setIsDesktopMenuOpen(false);
      navigate("/login");
    }
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const toggleDesktopMenu = () => {
    setIsDesktopMenuOpen(!isDesktopMenuOpen);
  };

  // Listen for navigation from Messages page to open menu and highlight Chats
  useEffect(() => {
    const state = location.state as any;
    if (state?.openMenu && state?.highlightChats) {
      setIsDesktopMenuOpen(true);
      setHighlightChats(true);

      // Clear the highlight after 2 seconds
      setTimeout(() => {
        setHighlightChats(false);
      }, 2000);
    }
  }, [location]);

  // fetch notifications on component mount
  useEffect(() => {
    if (user) {
      dispatch(fetchNotifications());
    }
  }, [user, dispatch]);

  // Handle clicks outside dropdowns to close them
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const desktopMenuDropdown = target.closest(".desktop-menu-dropdown");
      const mobileMenuOverlay = target.closest(".mobile-menu-overlay");
      const notificationDropdown = target.closest('.notification-dropdown');


      if (!desktopMenuDropdown && isDesktopMenuOpen) {
        setIsDesktopMenuOpen(false);
      }

      if (!notificationDropdown && isNotificationOpen) {
        setIsNotificationOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDesktopMenuOpen, isNotificationOpen]);

  // Sync unread count when notification dropdown is opened
  useEffect(() => {
    if (isNotificationOpen && user) {
      dispatch(fetchUnreadCount());
    }
  }, [isNotificationOpen, user, dispatch]);


  const isHomePage = location.pathname === "/";
  const hideHeaderOnMobile = location.pathname === "/bookmarks";

  const handleNotificationClick = async (notif: any, e?: React.MouseEvent) => {
    // Prevent event bubbling if event is passed
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (!notif) return;

    if (notif.id) {
      await markAsRead(notif.id);
    }

    // Message or reaction notifications -> open Messages page and target conversation
    if (isMessageOrReactionNotification(notif)) {
      const conversationId = getConversationIdFromNotification(notif);
      const actorId = getActorIdFromNotification(notif);

      if (conversationId) {
        navigate('/messages', {
          state: {
            conversationId: String(conversationId),
            fromNotification: true
          }
        });
      } else if (actorId) {
        navigate('/messages', {
          state: {
            sellerId: String(actorId),
            fromNotification: true
          }
        });
      } else {
        navigate('/messages', {
          state: {
            fromNotification: true
          }
        });
      }
      setIsNotificationOpen(false);
      return;
    }

    // Product availability or product-related notifications -> navigate to product or listings
    if (notif.type === 'product' && notif.meta?.productId) {
      setIsNotificationOpen(false);
      setTimeout(() => {
        navigate(`/product/${notif.meta.productId}`, { replace: false });
      }, 50);
      return;
    }

    // Fallback: go to full notifications page with the selected notification
    setIsNotificationOpen(false);
    setTimeout(() => {
      navigate('/notifications', {
        state: {
          fromHeaderDropdown: true,
          selectedNotificationId: notif.id
        }
      });
    }, 50);
  };

  return (
    <>
      {/* Overlay for Home page when menu is open */}
      {isHomePage && isDesktopMenuOpen && (
        <div
          className="fixed inset-0 z-40"
          style={{ backgroundColor: "#0000001A" }}
        />
      )}

      <header
        className={`shadow-sm sticky top-0 z-50 ${isProductDetailPage ? "lg:block hidden" : ""} ${hideHeaderOnMobile ? "hidden lg:block" : ""} ${!user ? "border-b border-orange-100" : ""}`}
        style={{ backgroundColor: user ? "#FFFFFF" : "#FFFBF5", fontFamily: "Poppins, sans-serif" }}
      >
        <div className="max-w-[90rem] mx-auto px-4 sm:px-6 md:px-8 lg:px-0">
          <div className="flex justify-between items-center h-20">
            {/* Logo - Positioned further left */}
            <div className="flex-shrink-0">
              <Link
                to="/"
                onClick={handleLogoClick}
                className="flex items-center focus:outline-none rounded transition-all duration-200"
                aria-label="BaoAfrik Home"
              >
                {/* Desktop: Full logo, Mobile: Icon only */}
                <img
                  src={logo}
                  alt="BaoAfrik - African Marketplace Logo"
                  className="hidden sm:block h-8 w-auto object-contain"
                  width="120"
                  height="32"
                />
                <img
                  src={logoIcon}
                  alt="BaoAfrik Logo"
                  className="sm:hidden h-10 w-10 object-contain"
                  width="40"
                  height="40"
                />
              </Link>
            </div>

            {/* Center Search Bar - Only show on product detail page */}
            {showSearchBar && (
              <div className="flex-1 max-w-2xl mx-6">
                <div className="flex items-center gap-2">
                  {/* Search Input */}
                  <div className="flex-1 relative">
                    <input
                      type="text"
                      placeholder="Search for products..."
                      className="w-full pl-4 pr-10 py-2 border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                    />
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                      <svg
                        className="w-4 h-4 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                      </svg>
                    </div>
                  </div>

                  {/* Category Dropdown */}
                  <select className="px-3 py-2 border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white text-sm">
                    <option value="">All Categories</option>
                    <option value="Food & Spices">Food & Spices</option>
                    <option value="Fashion & Textiles">Fashion & Textiles</option>
                    <option value="Beauty & Wellness">Beauty & Wellness</option>
                    <option value="Home & Decor">Home & Decor</option>
                    <option value="Books & Media">Books & Media</option>
                  </select>

                  {/* Place of Origin Input */}
                  <input
                    type="text"
                    placeholder="Place of origin"
                    className="px-3 py-2 border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm w-32"
                  />

                  {/* Location Input */}
                  <input
                    type="text"
                    placeholder="Location"
                    className="px-3 py-2 border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm w-24"
                  />

                  {/* Search Button */}
                  <Button
                    type="button"
                    unstyled
                    className="text-white px-4 py-2 rounded-full font-medium text-sm transition-colors duration-200"
                    style={{ backgroundColor: "#F9A825" }}
                    onMouseEnter={(e) =>
                    ((e.target as HTMLElement).style.backgroundColor =
                      "#E6941F")
                    }
                    onMouseLeave={(e) =>
                    ((e.target as HTMLElement).style.backgroundColor =
                      "#F9A825")
                    }
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </Button>
                </div>
              </div>
            )}

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-6 ml-auto">
              {user ? (
                // Logged in user buttons
                <>
                  {/* Language Toggle */}
                  {/* <div className="relative">
                    <Button type="button" unstyled
                      onClick={toggleLanguageDropdown}
                      className="flex items-center px-2.5 py-1 border rounded-lg bg-white text-sm font-normal hover:bg-gray-50 focus:outline-none transition-colors duration-200"
                      style={{ borderColor: "#E4E4E4", color: "#BABABA" }}
                    >
                      {selectedLanguage}
                      <img
                        src={arrowDownIcon}
                        alt="Arrow"
                        className="ml-1 w-4 h-4"
                      />
                    </Button>

                    {isLanguageDropdownOpen && (
                      <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-3 z-50">
                        <div className="flex items-center justify-between px-4 pb-3 border-b border-gray-100">
                          <h3 className="text-sm font-medium text-gray-700">
                            Language :
                          </h3>
                          <Button type="button" unstyled
                            onClick={() => setIsLanguageDropdownOpen(false)}
                            className="text-gray-400 hover:text-gray-600 focus:outline-none"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                          </Button>
                        </div>

                        <div className="py-2">
                          <Button type="button" unstyled
                            onClick={() => handleLanguageChange("EN")}
                            className="!justify-start w-full flex items-center px-4 py-2 text-sm transition-colors duration-200"
                            style={{
                              backgroundColor:
                                selectedLanguage === "EN"
                                  ? "#F0F8FE"
                                  : "transparent",
                              color:
                                selectedLanguage === "EN"
                                  ? "#64B5F6"
                                  : "#374151",
                            }}
                          >
                            English
                          </Button>
                          <Button type="button" unstyled
                            onClick={() => handleLanguageChange("FR")}
                            className="!justify-start w-full flex items-center px-4 py-2 text-sm transition-colors duration-200"
                            style={{
                              backgroundColor:
                                selectedLanguage === "FR"
                                  ? "#F0F8FE"
                                  : "transparent",
                              color:
                                selectedLanguage === "FR"
                                  ? "#64B5F6"
                                  : "#374151",
                            }}
                          >
                            French
                          </Button>
                        </div>
                      </div>
                    )}
                  </div> */}

                  {/* start selling button */}
                  {/* <Link
                    to="/register"
                    className="flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors"
                    style={{ backgroundColor: "#FEF6E9" }}
                  >
                    <img
                      src={basketIcon}
                      alt="Basket"
                      className="w-5 h-5"
                      style={{
                        filter:
                          "brightness(0) saturate(100%) invert(59%) sepia(94%) saturate(423%) hue-rotate(359deg) brightness(98%) contrast(98%)",
                      }}
                    />
                    <span
                      className="text-sm font-normal"
                      style={{ color: "#F9A825" }}
                    >
                      Start selling
                    </span>
                  </Link> */}

                  {/* Notification Icon */}
                  <div className="relative notification-dropdown">
                    <Button
                      type="button"
                      unstyled
                      onClick={() => {
                        setIsNotificationOpen(!isNotificationOpen);
                      }}
                      className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-gray-100 focus:outline-none transition-all duration-200 relative"
                      title="Notifications"
                      aria-label="View notifications"
                    >
                      <img
                        src={notificationIcon}
                        alt="Notifications"
                        className="w-6 h-6"
                        style={{ filter: 'brightness(0) saturate(100%) invert(42%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(95%) contrast(92%)' }}
                      />
                      {notificationCount > 0 && (
                        <span className="absolute top-0 right-0 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                          {notificationCount}
                        </span>
                      )}
                    </Button>

                    {/* Notification Dropdown */}
                    {/* Notification Dropdown is now rendered globally below */}
                  </div>

                  <Link
                    to="/account"
                    className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-full hover:ring-1 hover:ring-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200"
                    title="Profile"
                    aria-label="Go to profile page"
                  >
                    <img
                      src={user.profileImage || avatar}
                      alt="User Icon"
                      className="w-8 h-8 rounded-full object-cover"
                      width="32"
                      height="32"
                    />
                  </Link>

                  {/* Desktop Burger Menu */}
                  <div className="relative desktop-menu-dropdown">
                    <Button
                      type="button"
                      unstyled
                      onClick={toggleDesktopMenu}
                      className="p-3 rounded-md hover:bg-gray-100 focus:outline-none transition-colors duration-200"
                      style={{ color: "#171717" }}
                      aria-label="Open user menu"
                    >
                      <svg
                        className="w-7 h-7"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M4 6h16M4 12h16M4 18h16"
                        />
                      </svg>
                    </Button>

                    {/* Desktop Dropdown Menu */}
                    {isDesktopMenuOpen && (
                      <BaoDesktopMenuDropdown
                        user={user}
                        setIsDesktopMenuOpen={setIsDesktopMenuOpen}
                        highlightChats={highlightChats}
                        handleLogout={handleLogout}
                      />
                    )}
                  </div>
                </>
              ) : (
                // Not logged in buttons (includes visitor mode)
                <>
                  {/* Language Toggle */}
                  {/* <div className="relative">
                    <Button type="button" unstyled
                      onClick={toggleLanguageDropdown}
                      className="flex items-center px-2.5 py-1 border rounded-lg bg-white text-sm font-normal hover:bg-gray-50 focus:outline-none transition-colors duration-200"
                      style={{ borderColor: "#E4E4E4", color: "#BABABA" }}
                    >
                      {selectedLanguage}
                      <img
                        src={arrowDownIcon}
                        alt="Arrow"
                        className="ml-1 w-4 h-4"
                      />
                    </Button>

                    {isLanguageDropdownOpen && (
                      <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-3 z-50">
                        <div className="flex items-center justify-between px-4 pb-3 border-b border-gray-100">
                          <h3 className="text-sm font-medium text-gray-700">
                            Language :
                          </h3>
                          <Button type="button" unstyled
                            onClick={() => setIsLanguageDropdownOpen(false)}
                            className="text-gray-400 hover:text-gray-600 focus:outline-none"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                          </Button>
                        </div>

                        <div className="py-2">
                          <Button type="button" unstyled
                            onClick={() => handleLanguageChange("EN")}
                            className="!justify-start w-full flex items-center px-4 py-2 text-sm transition-colors duration-200"
                            style={{
                              backgroundColor:
                                selectedLanguage === "EN"
                                  ? "#F0F8FE"
                                  : "transparent",
                              color:
                                selectedLanguage === "EN"
                                  ? "#64B5F6"
                                  : "#374151",
                            }}
                          >
                            English
                          </Button>
                          <Button type="button" unstyled
                            onClick={() => handleLanguageChange("FR")}
                            className="!justify-start w-full flex items-center px-4 py-2 text-sm transition-colors duration-200"
                            style={{
                              backgroundColor:
                                selectedLanguage === "FR"
                                  ? "#F0F8FE"
                                  : "transparent",
                              color:
                                selectedLanguage === "FR"
                                  ? "#64B5F6"
                                  : "#374151",
                            }}
                          >
                            French
                          </Button>
                        </div>
                      </div>
                    )}
                  </div> */}

                  <Link
                    to="/login"
                    className="inline-flex items-center px-8 py-2 text-white rounded-lg font-medium text-sm transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2"
                    style={{ backgroundColor: "#F9A822" }}
                    onMouseEnter={(e) =>
                    ((e.target as HTMLElement).style.backgroundColor =
                      "#E6941F")
                    }
                    onMouseLeave={(e) =>
                    ((e.target as HTMLElement).style.backgroundColor =
                      "#F9A822")
                    }
                  >
                    Sign In
                  </Link>
                  <Link
                    to="/register"
                    className="inline-flex items-center px-8 py-2 rounded-lg font-medium text-sm transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 border"
                    style={{ borderColor: "#F9A822", color: "#F9A822" }}
                    onMouseEnter={(e) => {
                      (e.target as HTMLElement).style.backgroundColor =
                        "#FFF8F0";
                    }}
                    onMouseLeave={(e) => {
                      (e.target as HTMLElement).style.backgroundColor =
                        "transparent";
                    }}
                  >
                    Sign Up
                  </Link>
                </>
              )}
            </div>

            {/* Mobile Navigation */}
            <div className="md:hidden flex items-center space-x-4">
              {user ? (
                // Logged in user mobile navigation
                <>
                  {/* Language Toggle for mobile */}
                  {/* <div className="relative mr-1">
                    <Button type="button" unstyled
                      onClick={toggleLanguageDropdown}
                      className="flex items-center px-2.5 py-1 border rounded-lg bg-white text-sm font-normal hover:bg-gray-50 focus:outline-none transition-colors duration-200"
                      style={{ borderColor: '#E4E4E4', color: '#BABABA' }}
                    >
                      {selectedLanguage}
                      <img src={arrowDownIcon} alt="Arrow" className="ml-1 w-4 h-4" />
                    </Button>

                    {isLanguageDropdownOpen && (
                      <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-3 z-50">
                        <div className="flex items-center justify-between px-4 pb-3 border-b border-gray-100">
                          <h3 className="text-sm font-medium text-gray-700">Language :</h3>
                          <Button type="button" unstyled
                            onClick={() => setIsLanguageDropdownOpen(false)}
                            className="text-gray-400 hover:text-gray-600 focus:outline-none"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </Button>
                        </div>

                        <div className="py-2">
                          <Button type="button" unstyled
                            onClick={() => handleLanguageChange('EN')}
                            className="!justify-start w-full flex items-center px-4 py-2 text-sm transition-colors duration-200"
                            style={{
                              backgroundColor: selectedLanguage === 'EN' ? '#F0F8FE' : 'transparent',
                              color: selectedLanguage === 'EN' ? '#64B5F6' : '#374151'
                            }}
                          >
                            English
                          </Button>
                          <Button type="button" unstyled
                            onClick={() => handleLanguageChange('FR')}
                            className="!justify-start w-full flex items-center px-4 py-2 text-sm transition-colors duration-200"
                            style={{
                              backgroundColor: selectedLanguage === 'FR' ? '#F0F8FE' : 'transparent',
                              color: selectedLanguage === 'FR' ? '#64B5F6' : '#374151'
                            }}
                          >
                            French
                          </Button>
                        </div>
                      </div>
                    )}
                  </div> */}

                  {/* Become a seller button - only for logged in users */}
                  {/* <Link
                    to="/register"
                    className="flex items-center space-x-1.5 px-2.5 py-2 rounded-lg transition-colors mr-1"
                    style={{ backgroundColor: '#FEF6E9' }}
                  >
                    <img src={basketIcon} alt="Basket" className="w-5 h-5" style={{ filter: 'brightness(0) saturate(100%) invert(59%) sepia(94%) saturate(423%) hue-rotate(359deg) brightness(98%) contrast(98%)' }} />
                    <span className="text-sm font-normal" style={{ color: '#F9A825' }}>Start selling</span>
                  </Link> */}

                  {/* Notification Icon */}
                  <Button
                    type="button"
                    unstyled
                    onClick={() => setIsNotificationOpen(!isNotificationOpen)}
                    className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-100 focus:outline-none transition-all duration-200 relative"
                    title="Notifications"
                    aria-label="View notifications"
                  >
                    <img
                      src={notificationIcon}
                      alt="Notifications"
                      className="w-5 h-5"
                      style={{ filter: 'brightness(0) saturate(100%) invert(42%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(95%) contrast(92%)' }}
                    />
                  </Button>

                  {/* Burger Menu Button */}
                  <Button
                    type="button"
                    unstyled
                    onClick={toggleMobileMenu}
                    className="p-1.5 rounded-md focus:outline-none transition-all duration-200"
                    style={{ color: '#171717' }}
                    aria-label="Toggle navigation menu"
                    aria-expanded={isMobileMenuOpen}
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                      {isMobileMenuOpen ? (
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      ) : (
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                      )}
                    </svg>
                  </Button>
                </>
              ) : (
                // Logged out user mobile navigation
                <>
                  {/* Language Toggle for mobile */}
                  <div className="relative" style={{ marginRight: window.innerWidth < 640 ? '8px' : '4px' }}>
                    {/* <Button type="button" unstyled
                      onClick={toggleLanguageDropdown}
                      className="flex items-center border rounded-lg bg-white font-normal hover:bg-gray-50 focus:outline-none transition-colors duration-200"
                      style={{
                        borderColor: '#E4E4E4',
                        color: '#BABABA',
                        padding: window.innerWidth < 640 ? '4px 8px' : '6px 10px',
                        fontSize: window.innerWidth < 640 ? '11px' : '14px'
                      }}
                    >
                      {selectedLanguage}
                      <img src={arrowDownIcon} alt="Arrow" className="ml-1" style={{ width: window.innerWidth < 640 ? '12px' : '16px', height: window.innerWidth < 640 ? '12px' : '16px' }} />
                    </Button> */}

                    {/* Language Dropdown for mobile */}
                    {/* {isLanguageDropdownOpen && ( */}
                    {/* <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-3 z-50"> */}
                    {/* <div className="flex items-center justify-between px-4 pb-3 border-b border-gray-100">
                          <h3 className="text-sm font-medium text-gray-700">Language :</h3>
                          <Button type="button" unstyled
                            onClick={() => setIsLanguageDropdownOpen(false)}
                            className="text-gray-400 hover:text-gray-600 focus:outline-none"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </Button>
                        </div> */}

                    {/* Language Options */}
                    {/* <div className="py-2">
                          <Button type="button" unstyled
                            onClick={() => handleLanguageChange('EN')}
                            className={`w-full flex items-center px-4 py-2 text-sm transition-colors duration-200 ${selectedLanguage === 'EN'
                              ? 'bg-blue-50 text-blue-600'
                              : 'text-gray-700 hover:bg-gray-50'
                              }`}
                          >
                            <img
                              src="https://flagcdn.com/w20/gb.png"
                              alt="UK flag"
                              className="w-5 h-5 mr-3 object-cover rounded-full"
                              width="20"
                              height="20"
                            />
                            English
                          </Button>
                          <Button type="button" unstyled
                            onClick={() => handleLanguageChange('FR')}
                            className={`w-full flex items-center px-4 py-2 text-sm transition-colors duration-200 ${selectedLanguage === 'FR'
                              ? 'bg-blue-50 text-blue-600'
                              : 'text-gray-700 hover:bg-gray-50'
                              }`}
                          >
                            French
                          </Button>
                          <Button type="button" unstyled
                            onClick={() => handleLanguageChange('DE')}
                            className="!justify-start w-full flex items-center px-4 py-2 text-sm transition-colors duration-200"
                            style={{
                              backgroundColor: selectedLanguage === 'DE' ? '#F0F8FE' : 'transparent',
                              color: selectedLanguage === 'DE' ? '#64B5F6' : '#374151'
                            }}
                          >
                            <img
                              src="https://flagcdn.com/w20/de.png"
                              alt="Germany flag"
                              className="w-5 h-5 mr-3 object-cover rounded-full"
                              width="20"
                              height="20"
                            />
                            Germany
                          </Button>
                          <Button type="button" unstyled
                            onClick={() => handleLanguageChange('ES')}
                            className="!justify-start w-full flex items-center px-4 py-2 text-sm transition-colors duration-200"
                            style={{
                              backgroundColor: selectedLanguage === 'ES' ? '#F0F8FE' : 'transparent',
                              color: selectedLanguage === 'ES' ? '#64B5F6' : '#374151'
                            }}
                          >
                            <img
                              src="https://flagcdn.com/w20/es.png"
                              alt="Spain flag"
                              className="w-5 h-5 mr-3 object-cover rounded-full"
                              width="20"
                              height="20"
                            />
                            Spanish
                          </Button>
                        </div> */}
                    {/* </div> */}
                    {/* )} */}
                  </div>

                  {/* Sign In Button - Mobile */}
                  <Link
                    to="/login"
                    className="inline-flex items-center text-white rounded-lg font-medium transition-colors duration-200 focus:outline-none"
                    style={{
                      backgroundColor: '#F9A822',
                      padding: window.innerWidth < 640 ? '4px 12px' : '8px 32px',
                      fontSize: window.innerWidth < 640 ? '11px' : '14px',
                      marginRight: window.innerWidth < 640 ? '8px' : '4px'
                    }}
                    onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = '#E6941F'}
                    onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = '#F9A822'}
                  >
                    Sign In
                  </Link>

                  {/* Sign Up Button - Mobile */}
                  <Link
                    to="/register"
                    className="inline-flex items-center rounded-lg font-medium transition-colors duration-200 focus:outline-none border"
                    style={{
                      borderColor: '#F9A822',
                      color: '#F9A822',
                      padding: window.innerWidth < 640 ? '4px 12px' : '8px 32px',
                      fontSize: window.innerWidth < 640 ? '11px' : '14px'
                    }}
                    onMouseEnter={(e) => {
                      (e.target as HTMLElement).style.backgroundColor = '#FFF8F0';
                    }}
                    onMouseLeave={(e) => {
                      (e.target as HTMLElement).style.backgroundColor = 'transparent';
                    }}
                  >
                    Sign Up
                  </Link>
                </>
              )}
            </div>
          </div>

          {/* Mobile Menu Dropdown - Full Screen Overlay */}
          {isMobileMenuOpen && (
            <BaoMobileMenuDropdown
              user={user}
              toggleMobileMenu={toggleMobileMenu}
              setIsMobileMenuOpen={setIsMobileMenuOpen}
              highlightChats={highlightChats}
              handleLogout={handleLogout}
            />
          )}

          {/* Notification Dropdown */}
          {isNotificationOpen && (
            <BaoNotificationDropdown
              setIsNotificationOpen={setIsNotificationOpen}
              notificationTab={notificationTab}
              setNotificationTab={setNotificationTab}
              filteredNotifications={filteredNotifications}
              markAllAsRead={markAllAsRead}
              handleNotificationClick={handleNotificationClick}
            />
          )}
        </div>
      </header>
    </>
  );
};

export default Header;
